import { createApi } from '@reduxjs/toolkit/query/react'
import { dynamicBaseQuery } from './helpers'
import { BikeOwnershipCreditOverview } from '../../types/webshop'

interface PurchaseSessionResponse {
    url: string
}

export interface SubscriptionSessionResponse {
    session_url: string
}

export interface NafCustomerResponse {
    customer: {
        party_id: string
        customer_id: string
        customer_type: string
        naf_member_id: string
        subscription_id: string
        current_period_end: Date
        pricing_type: string
    } | null
}

export interface SubscriptionBikeAddResponse {
    bike_ownership_credit_id: string
}

export interface ActivateBikeCreditInput {
    bikeOwnershipId: string
    bikeCreditId: string
}

export interface BikeCredit {
    id: string
    party_id: string
    bike_ownership_id: string | null
    subscription_reepay_id: string
    created_on: Date
    modified_on: Date | null
    has_shipped?: boolean
}

export interface BikeCreditResponse {
    credits: BikeCredit[]
    current_period_end: Date
}

// Define a service using a base URL and expected endpoints
export const webshopApi = createApi({
    reducerPath: 'webshopApi',
    baseQuery: dynamicBaseQuery,
    tagTypes: ['BikekeyPurchaseSession', 'bikeCredits', 'subscriptionSessions', 'nafCustomer'],
    endpoints: (builder) => ({
        createBikekeyPurchaseSession: builder.query<PurchaseSessionResponse, string>({
            query: (configId: string) => ({
                url: `/webshop/orders/initiations`,
                params: { config_id: configId },
                method: 'POST',
            }),
        }),
        createBikekeySubscriptionSession: builder.mutation<
            SubscriptionSessionResponse,
            string | null
        >({
            query: (already_activated_plate_id: string | null) => ({
                url: `/webshop/subscriptions/sessions`,
                body: { already_activated_plate_id },
                method: 'POST',
            }),
        }),
        createBikekeySubscriptionBike: builder.mutation<SubscriptionBikeAddResponse, undefined>({
            query: () => ({
                url: `/webshop/subscriptions/bikes`,
                method: 'POST',
            }),
            invalidatesTags: ['bikeCredits'],
        }),
        getBikeCredits: builder.query<BikeCreditResponse, undefined>({
            query: () => ({
                url: `/webshop/bike-activations`,
                method: 'GET',
            }),
            providesTags: ['bikeCredits'],
        }),
        activateBikeCredit: builder.mutation<undefined, ActivateBikeCreditInput>({
            query: (body: ActivateBikeCreditInput) => ({
                url: `/webshop/bike-activations/${body.bikeCreditId}`,
                body: { bike_ownership_id: body.bikeOwnershipId },
                method: 'POST',
            }),
            invalidatesTags: ['bikeCredits'],
        }),
        createCustomerPortalSession: builder.mutation<SubscriptionSessionResponse, undefined>({
            query: () => ({
                url: `/webshop/customers/sessions`,
                body: {},
                method: 'POST',
            }),
            invalidatesTags: ['subscriptionSessions'],
        }),
        getAvailableCreditsOverview: builder.query<
            { credits: BikeOwnershipCreditOverview[] },
            string
        >({
            query: (configId: string) => ({
                url: `/webshop/bike-ownership-credits/overview`,
                method: 'GET',
            }),
        }),
        createNafCustomerPortalSession: builder.mutation<SubscriptionSessionResponse, undefined>({
            query: () => ({
                url: `/webshop/naf/`,
                method: 'POST',
            }),
            invalidatesTags: ['nafCustomer'],
        }),
        createPurchaseExtraBikeRegistrationSession: builder.mutation<
            { session_url: string },
            undefined
        >({
            query: () => ({
                url: `/webshop/naf/extra-bike-registration`,
                method: 'POST',
            }),
        }),
        getNafCustomer: builder.query<NafCustomerResponse, undefined>({
            query: () => ({
                url: `/webshop/naf/`,
                method: 'GET',
            }),
            providesTags: ['nafCustomer'],
        }),
        createCustomerSessionForPricingTable: builder.mutation<
            {
                client_secret: string
                product_info: { pricing_table_id: string; pricing_type: string }
            },
            undefined
        >({
            query: () => ({
                url: `/webshop/customer-session-for-pricing-table`,
                method: 'POST',
            }),
        }),
        applyMemberDiscount: builder.mutation<undefined, { naf_member_id: string }>({
            query: (body: { naf_member_id: string }) => ({
                url: `/webshop/naf/apply-member-discount/${body.naf_member_id}`,
                method: 'POST',
            }),
            invalidatesTags: ['nafCustomer'],
        }),
    }),
})

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
    useCreateBikekeyPurchaseSessionQuery,
    useCreateBikekeySubscriptionSessionMutation,
    useCreateBikekeySubscriptionBikeMutation,
    useGetBikeCreditsQuery,
    useActivateBikeCreditMutation,
    useCreateCustomerPortalSessionMutation,
    useGetAvailableCreditsOverviewQuery,
    useCreateNafCustomerPortalSessionMutation,
    useGetNafCustomerQuery,
    useCreateCustomerSessionForPricingTableMutation,
    useApplyMemberDiscountMutation,
    useCreatePurchaseExtraBikeRegistrationSessionMutation,
} = webshopApi
